/**
 * What the push service worker does, as plain functions over the few parts of the worker scope they
 * touch, so they can be tested without a browser (push design 9.1, FR-NT-064 to FR-NT-066).
 *
 * The worker needs no Firebase code: FCM hands the browser an ordinary Web Push message whose JSON
 * carries our data under `data`. Keeping Firebase out keeps the worker small (NFR-NT-017) and means
 * nothing in it can be changed by a dependency update.
 */

/** What the server's PushPayloadBuilder sends; every value a string (BR-NT-018). */
export interface PushData {
  v: string;
  id: string;
  type: string;
  kind: string;
  priority: string;
  title: string;
  body?: string;
  link?: string;
  tag: string;
  tenant: string;
  unread: string;
}

/** The message a focused tab receives instead of an OS notification (FR-NT-065). */
export const MESSAGE_TYPE = "simpro.notification";

/** Three long buzzes with short gaps, in milliseconds: an alarm, not a tap. */
export const HIGH_PRIORITY_VIBRATION = [400, 150, 400, 150, 400];

export interface PushMessageToPage {
  type: typeof MESSAGE_TYPE;
  notification: PushData;
}

export interface WorkerClient {
  readonly url: string;
  readonly focused: boolean;
  readonly visibilityState: string;
  postMessage(message: unknown): void;
  focus(): Promise<unknown>;
  navigate(url: string): Promise<unknown>;
}

export interface WorkerScope {
  readonly origin: string;
  clients: {
    matchAll(options: { type: "window"; includeUncontrolled: boolean }): Promise<readonly WorkerClient[]>;
    openWindow(url: string): Promise<unknown>;
  };
  showNotification(title: string, options: Record<string, unknown>): Promise<void>;
  setAppBadge?(count: number): Promise<void>;
}

const SUPPORTED_VERSION = "1";
const MAX_TEXT = 1000;

/**
 * The payload, from FCM's envelope (`{ data: {...} }`) or bare, or null when it is not ours or not a
 * version this worker knows: an unknown shape is dropped rather than shown half-understood.
 */
export function readPushData(raw: unknown): PushData | null {
  const outer = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : null;
  const inner = outer && typeof outer.data === "object" && outer.data !== null ? outer.data : outer;
  if (!inner) return null;

  const data = inner as Record<string, unknown>;
  const text = (key: string): string | undefined => {
    const value = data[key];
    return typeof value === "string" ? value.slice(0, MAX_TEXT) : undefined;
  };

  const v = text("v");
  const id = text("id");
  const title = text("title");
  if (v !== SUPPORTED_VERSION || !id || !title) return null;

  return {
    v,
    id,
    title,
    type: text("type") ?? "",
    kind: text("kind") ?? "StatusUpdate",
    priority: text("priority") ?? "Normal",
    body: text("body"),
    link: text("link"),
    tag: text("tag") ?? id,
    tenant: text("tenant") ?? "",
    unread: text("unread") ?? "0",
  };
}

/**
 * Shows the push, or hands it to the tab the person is looking at (FR-NT-064, FR-NT-065). A focused
 * tab gets a message so the app can show a toast and refresh the bell; an OS notification on top of
 * the page you are reading would be noise.
 */
export async function handlePush(scope: WorkerScope, raw: unknown): Promise<void> {
  const data = readPushData(raw);
  if (!data) return;

  const tabs = await scope.clients.matchAll({ type: "window", includeUncontrolled: true });
  const watching = tabs.find((tab) => tab.focused && tab.visibilityState === "visible");

  if (watching) {
    const message: PushMessageToPage = { type: MESSAGE_TYPE, notification: data };
    watching.postMessage(message);
  } else {
    await scope.showNotification(data.title, {
      body: data.body,
      tag: data.tag,
      renotify: true,
      requireInteraction: data.priority === "High",
      // An alarm's rhythm on a phone that vibrates (Android): a booking request waiting is High
      // (TDD-SA FR-SA-183). A web app cannot choose its own sound; the system's plays, and `renotify`
      // plays it again for every reminder.
      ...(data.priority === "High" ? { vibrate: HIGH_PRIORITY_VIBRATION, silent: false } : {}),
      // Each app that serves the worker ships these two at its root (its manifest test checks).
      icon: "/icon-192.png",
      badge: "/badge-72.png",
      data: { id: data.id, link: data.link },
    });
  }

  const unread = Number(data.unread);
  if (scope.setAppBadge && Number.isFinite(unread) && unread >= 0) {
    await scope.setAppBadge(unread).catch(() => undefined);
  }
}

/**
 * Where a click goes: the notification's link inside this app, with `n` so the app marks it read
 * (FR-NT-066). Anything that is not a plain path on this origin goes to the inbox instead: a push
 * is never a way out of the app (SEC-NT-020).
 */
export function targetUrl(origin: string, id: string, link?: string): string {
  const fallback = new URL("/notifications", origin);
  let url = fallback;
  if (link && link.startsWith("/") && !link.startsWith("//")) {
    const candidate = new URL(link, origin);
    if (candidate.origin === origin) url = candidate;
  }

  url.searchParams.set("n", id);
  return url.toString();
}

/** Focuses a tab of the app and takes it to the target, or opens one (FR-NT-066). */
export async function handleClick(scope: WorkerScope, notificationData: unknown): Promise<void> {
  const data = (notificationData ?? {}) as { id?: unknown; link?: unknown };
  if (typeof data.id !== "string") return;

  const url = targetUrl(scope.origin, data.id, typeof data.link === "string" ? data.link : undefined);
  const tabs = await scope.clients.matchAll({ type: "window", includeUncontrolled: true });
  const tab = tabs.find((t) => new URL(t.url).origin === scope.origin);

  if (tab) {
    await tab.navigate(url).catch(() => undefined);
    await tab.focus();
    return;
  }

  await scope.clients.openWindow(url);
}
